import { z } from "zod";
import { Decision, IsoDateTime, PlayerRef } from "../common.ts";
import { OperatorFilter } from "./common.ts";

/** `GET /explorer/v1/search` (spec explorer-home-search; `explorer.read`). */
export const SearchQueryV1 = z
  .object({
    /** `player_ref`, `device_ref`, `verification_id` or IP address (trimmed). */
    q: z.string().trim().min(1).max(128),
    operator_id: OperatorFilter.optional(),
  })
  .strict()
  .meta({ id: "SearchQueryV1" });
export type SearchQueryV1 = z.infer<typeof SearchQueryV1>;

export const SearchHitV1 = z
  .discriminatedUnion("type", [
    z
      .object({
        type: z.literal("player"),
        operator_id: z.uuid(),
        player_ref: PlayerRef,
        last_seen_at: IsoDateTime,
      })
      .strict(),
    z
      .object({
        type: z.literal("device"),
        operator_id: z.uuid(),
        device_ref: z.uuid(),
        last_seen_at: IsoDateTime,
      })
      .strict(),
    z
      .object({
        type: z.literal("transaction"),
        operator_id: z.uuid(),
        verification_id: z.uuid(),
        player_ref: PlayerRef,
        decision: Decision,
        occurred_at: IsoDateTime,
      })
      .strict(),
    z
      .object({
        type: z.literal("ip"),
        /** Canonical text form of the address. */
        ip: z.string(),
        /** Transactions from the IP in the last 180 days (the IP retention), all allowed operators. */
        transaction_count: z.int().positive(),
      })
      .strict(),
  ])
  .meta({ id: "SearchHitV1" });
export type SearchHitV1 = z.infer<typeof SearchHitV1>;

/**
 * Search result: `open` when exactly one result (the SPA opens it directly), otherwise the results
 * grouped by type. "No results" never reveals whether the identifier exists in another operator.
 * A MAC address is `unsupported` with `mac_not_collected` (browsers never collect it).
 */
export const SearchResponseV1 = z
  .object({
    query: z.string(),
    unsupported: z
      .object({ cause: z.literal("mac_not_collected") })
      .strict()
      .optional(),
    open: SearchHitV1.nullable(),
    groups: z
      .object({
        players: z.array(SearchHitV1),
        devices: z.array(SearchHitV1),
        transactions: z.array(SearchHitV1),
        ips: z.array(SearchHitV1),
      })
      .strict(),
  })
  .strict()
  .meta({ id: "SearchResponseV1" });
export type SearchResponseV1 = z.infer<typeof SearchResponseV1>;
